import moment from 'moment'
import 'moment/locale/es'
import { cuentaMask, dineroDisabledMask } from '../Components/Items'
import { PDF_COLORS, seccionHeadingV2 } from '../Components/PdfComponents/componentsv2.0/stylesPDFv2'
import { PDFCompanyInfoV2 } from '../Components/PdfComponents/componentsv2.0/PDFCompanyInfo'
import { PDFDataTableV2 } from '../Components/PdfComponents/componentsv2.0/PDFDataTable'
import { PDFSummarySectionV2 } from '../Components/PdfComponents/componentsv2.0/PDFTotalsSummary'

moment.locale('es')

/*
  Las pólizas contables en formato compacto: por cada una, un encabezado de tres columnas
  (fecha/concepto | números de póliza | tipo/proyecto), la tabla de cargos y abonos, y el
  renglón de totales. Van una tras otra, sin salto de página.

  Este es el formato que estrenó el reporte de Reposición de Caja Chica y que se reusa en la
  hoja de trazabilidad del modal "Pólizas del Movimiento". No es el formato oficial de póliza
  (ése vive en polizaPdf.js y se imprime una hoja por póliza): aquí se busca que quepan varias
  de un vistazo.

  Cada elemento de `polizas` es una respuesta de polizas/datosPolizas, es decir { poliza,
  cargosabonos }, lo mismo que consume polizaPdf. Devuelve un arreglo de bloques pdfmake listo
  para hacerle spread dentro de un `content`, o vacío si no hay nada que pintar.

  `opciones.moderno` pinta los mismos datos con el lenguaje visual del diseño v2, para que la
  hoja no se vea clásica dentro de un documento moderno. Es opt-in: el contenedor tiene que
  traer los estilos v2 (pdfStylesV2) y la tipografía Mulish, así que sólo lo prende quien ya es
  un documento v2 — hoy polizasTrazabilidadPdfV2. El reporte de caja chica sigue en clásico.

  Los estilos que usa el modo clásico y que el documento contenedor debe declarar:
  'tableHeader', 'subheader', 'textoChico', 'tituloTabla' y 'tableExample'.
  Los del modo moderno, todos presentes en pdfStylesV2: 'tituloTabla' y 'textoTablaChico'.
*/
const seccionPolizasContables = (polizas, opciones = {}) => {
    const listaPolizas = polizas || []
    if (listaPolizas.length === 0) return []

    const titulo = opciones.titulo === undefined ? 'Pólizas Contables' : opciones.titulo
    const moderno = !!opciones.moderno
    const estiloCelda = moderno ? 'textoTablaChico' : 'textoChico'

    const bloques = []
    if (titulo) {
        bloques.push(moderno
            ? seccionHeadingV2(titulo)
            : { text: titulo, style: 'tableHeader', margin: [0, 10, 0, 5] })
    }

    for (const valuePolizaContable of listaPolizas) {
        const poliza = valuePolizaContable?.poliza
        if (!poliza) continue
        const cargosabonos = valuePolizaContable.cargosabonos || []

        let ied
        let iedInicial
        const tipoDePolizaId = poliza.tipodepoliza?.tipoDePolizaId
        if (tipoDePolizaId === 1) { ied = 'Ingreso'; iedInicial = 'I' }
        if (tipoDePolizaId === 2) { ied = 'Egreso'; iedInicial = 'E' }
        if (tipoDePolizaId === 3) { ied = 'Traslado'; iedInicial = 'T' }
        if (tipoDePolizaId === 4) { ied = 'Nomina'; iedInicial = 'N' }
        if (tipoDePolizaId === 5) { ied = 'Pago'; iedInicial = 'P' }
        if (tipoDePolizaId === 6) { ied = 'Diario'; iedInicial = 'D' }

        let cargosTotal = 0
        let abonosTotal = 0
        const cargosAbonosTable = cargosabonos.map(valueCA => {
            let cargos = 0
            let abonos = 0
            if (valueCA.cargo) {
                cargos = valueCA.monto
                cargosTotal += valueCA.monto
            } else {
                abonos = valueCA.monto
                abonosTotal += valueCA.monto
            }
            // La cuenta puede venir nula del backend y nivel1..nivel5 son String con default
            // null en el modelo: sin el `|| ''` la concatenación imprime "null".
            const cuenta = valueCA.cuentacontable
            const codigoCuenta = cuenta
                ? `${cuenta.nivel1 || ''}${cuenta.nivel2 || ''}${cuenta.nivel3 || ''}${cuenta.nivel4 || ''}${cuenta.nivel5 || ''}`
                : ''
            return [
                { text: codigoCuenta ? cuentaMask(codigoCuenta) : '', style: estiloCelda, alignment: 'left' },
                { text: cuenta?.descripcion || '', style: estiloCelda, alignment: 'left' },
                { text: valueCA.concepto || '', style: estiloCelda, alignment: 'left' },
                { text: cargos === 0 ? '-' : dineroDisabledMask(cargos), style: estiloCelda, alignment: 'right' },
                { text: abonos === 0 ? '-' : dineroDisabledMask(abonos), style: estiloCelda, alignment: 'right' },
            ]
        })

        const fechaTexto = poliza.fechaRegistro ? moment(poliza.fechaRegistro).locale('es').format('L') : ''
        const proyectoTexto = poliza.proyecto?.nombre
            ? poliza.proyecto.nombre + (poliza.proyecto.descripcion ? ' - ' + poliza.proyecto.descripcion : '')
            : ''

        // Este formato no lleva marca de agua, así que la cancelación se anuncia con un letrero.
        // La trazabilidad no filtra por estado a propósito: las canceladas son parte de la historia.
        if (poliza.estado === 0) {
            bloques.push({
                text: 'PÓLIZA CANCELADA',
                color: moderno ? PDF_COLORS.danger : '#cf1322',
                bold: true,
                fontSize: 10,
                margin: [0, 6, 0, 0],
            })
        }

        if (moderno) {
            bloques.push(
                // El tipo y el número van en el rótulo: identifican la póliza de un vistazo
                // cuando la hoja trae varias seguidas.
                seccionHeadingV2(
                    (ied ? 'Póliza de ' + ied : 'Póliza')
                    + (iedInicial ? ' (' + iedInicial + ')' : '')
                    + (poliza.numeroTipoTipoPoliza ? ' N° ' + poliza.numeroTipoTipoPoliza : '')
                ),
                PDFCompanyInfoV2({
                    left: [
                        { label: 'Fecha:', value: fechaTexto },
                        { label: 'Concepto:', value: poliza.concepto || '' },
                    ],
                    right: [
                        {
                            label: (poliza.tipodepoliza?.descripcion || 'Póliza') + ':',
                            value: String(poliza.numeroTipoPoliza || ''),
                        },
                        { label: 'Proyecto:', value: proyectoTexto },
                    ],
                    labelWidthLeft: 'auto',
                    labelWidthRight: 'auto',
                }),
                PDFDataTableV2({
                    widths: ['14%', '35%', '31%', '10%', '10%'],
                    title: [
                        { text: 'Cuenta', style: 'tituloTabla', alignment: 'left' },
                        { text: 'Descripción', style: 'tituloTabla', alignment: 'left' },
                        { text: 'Concepto', style: 'tituloTabla', alignment: 'left' },
                        { text: 'Cargo', style: 'tituloTabla', alignment: 'right' },
                        { text: 'Abono', style: 'tituloTabla', alignment: 'right' },
                    ],
                    rows: cargosAbonosTable,
                }),
                // Sin fila TOTAL destacada: en una hoja con varias pólizas el énfasis se lo
                // llevaría cada cierre y competiría con los rótulos.
                PDFSummarySectionV2({
                    rows: [
                        { label: 'Total cargos:', value: dineroDisabledMask(cargosTotal) },
                        { label: 'Total abonos:', value: dineroDisabledMask(abonosTotal) },
                    ],
                    emphasizeLast: false,
                })
            )
            continue
        }

        bloques.push(
            {
                style: 'tableExample',
                margin: [0, 5, 0, 2],
                table: {
                    widths: ['40%', '30%', '30%'],
                    layout: { defaultBorder: false },
                    body: [
                        [
                            {
                                table: {
                                    widths: ['30%', '70%'],
                                    layout: { defaultBorder: false },
                                    body: [
                                        [{ text: 'Fecha :', style: 'subheader' }, { text: fechaTexto, style: 'textoChico' }],
                                        [{ text: 'Concepto :', style: 'subheader' }, { text: poliza.concepto || '', style: 'textoChico' }],
                                    ]
                                },
                                layout: 'noBorders',
                            },
                            {
                                table: {
                                    widths: ['30%', '70%'],
                                    layout: { defaultBorder: false },
                                    body: [
                                        [{ text: 'N. Poliza:', style: 'subheader' }, { text: (ied || '') + ': ' + (poliza.numeroTipoTipoPoliza || ''), style: 'textoChico' }],
                                        [{ text: 'N. Poliza:', style: 'subheader' }, { text: (poliza.tipodepoliza?.descripcion || '') + ': ' + (poliza.numeroTipoPoliza || ''), style: 'textoChico' }],
                                    ]
                                },
                                layout: 'noBorders',
                            },
                            {
                                table: {
                                    widths: ['30%', '70%'],
                                    layout: { defaultBorder: false },
                                    body: [
                                        [{ text: 'Tipo Poliza: ', style: 'subheader' }, { text: iedInicial ? '(' + iedInicial + ') ' + ied : '', style: 'textoChico' }],
                                        // Una póliza puede no traer proyecto: el acceso pelón truena.
                                        [{ text: 'Proyecto: ', style: 'subheader' }, { text: proyectoTexto, style: 'textoChico' }],
                                    ]
                                },
                                layout: 'noBorders',
                            },
                        ]
                    ]
                },
                layout: {
                    hLineColor: () => '#adaaaa',
                    vLineColor: () => '#adaaaa',
                    hLineStyle: (i, node) => (i === 0 || i === node.table.body.length) ? null : { dash: { length: 2, space: 1 } },
                    vLineStyle: (i, node) => (i === 0 || i === node.table.widths.length) ? null : { dash: { length: 2 } },
                }
            },
            {
                style: 'tableExample',
                table: {
                    widths: ['14%', '35%', '31%', '10%', '10%'],
                    body: [
                        [
                            { text: 'Cuenta', style: 'tituloTabla' },
                            { text: 'Descripción', style: 'tituloTabla' },
                            { text: 'Concepto', style: 'tituloTabla' },
                            { text: 'Cargo', style: 'tituloTabla' },
                            { text: 'Abono', style: 'tituloTabla' },
                        ],
                        ...cargosAbonosTable
                    ]
                },
                layout: {
                    fillColor: (rowIndex) => rowIndex % 2 === 0 ? "#f5f5f5" : "#ffffff",
                    hLineColor: () => '#adaaaa',
                    vLineColor: () => '#adaaaa',
                    hLineStyle: (i, node) => (i === 0 || i === node.table.body.length) ? null : { dash: { length: 2, space: 1 } },
                    vLineStyle: (i, node) => (i === 0 || i === node.table.widths.length) ? null : { dash: { length: 2 } },
                }
            },
            {
                alignment: 'right',
                table: {
                    widths: ['15%', '30%', '25%', '10%', '10%', '10%'],
                    body: [[
                        { border: [false, false, false, false], text: '' }, { border: [false, false, false, false], text: '' }, { border: [false, false, false, false], text: '' },
                        { border: [false, false, false, false], text: 'Total:', style: 'subheader' },
                        { border: [false, false, false, false], text: dineroDisabledMask(cargosTotal), style: 'textoChico' },
                        { border: [false, false, false, false], text: dineroDisabledMask(abonosTotal), style: 'textoChico', alignment: 'right' }
                    ]]
                },
            }
        )
    }

    return bloques
}

export default seccionPolizasContables
